import { useEffect, useRef, useState } from 'react';
import { Share2, Copy, Check } from 'lucide-react';
import { formatDuration } from '@boatnav/core';
import { useBoatStore } from '../boat/boatStore.js';
import { startShare, stopShare, shareUrl } from './shareService.js';
import { useShareState } from './useShare.js';
import { msg } from '../../lib/messages.js';
import './share.css';

interface ShareSheetProps {
  onClose: () => void;
}

/** Share button sheet on the Navigate top bar (spec 15): off = explainer +
 *  start; on = link + time left + stop. Privacy copy is load-bearing — it's
 *  the whole model in one sentence. */
export function ShareSheet({ onClose }: ShareSheetProps): JSX.Element {
  const { loaded, load, boats, activeBoatId } = useBoatStore();
  const share = useShareState();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [, forceTick] = useState(0);
  const url = share.id ? shareUrl(share.id) : '';
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);

  // Keep "time left" fresh while the sheet is open.
  useEffect(() => {
    if (!share.active) return;
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [share.active]);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const boat = boats.find((b) => b.id === activeBoatId) ?? null;

  const start = async (): Promise<void> => {
    setBusy(true);
    setError(false);
    try {
      const { url: startedUrl } = await startShare(boat?.name, 24);
      if (navigator.share) {
        try {
          await navigator.share({ url: startedUrl, title: msg.share.title });
        } catch {
          /* user cancelled the native share sheet — link is still active */
        }
      }
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable (old browser) — the link is still visible/selectable */
    }
  };

  const stop = async (): Promise<void> => {
    setBusy(true);
    await stopShare();
    setBusy(false);
    onClose();
  };

  const minutesLeft = share.expiresAt ? Math.max(0, (share.expiresAt - Date.now()) / 60_000) : 0;

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet share-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={msg.share.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__handle" />
        <h2 className="sheet__title">{msg.share.title}</h2>

        {!share.active ? (
          <>
            <p className="sheet__body">{msg.share.explain}</p>
            <p className="share-sheet__privacy">{msg.share.privacyNote}</p>
            {error && <p className="share-sheet__error">{msg.share.startFailed}</p>}
            <div className="sheet__actions">
              <button type="button" className="btn btn--accent" onClick={() => void start()} disabled={busy}>
                {busy ? msg.share.starting : msg.share.start}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="share-sheet__privacy">{msg.share.privacyNote}</p>
            <div className="share-sheet__link-row">
              <input className="share-sheet__link" value={url} readOnly onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="share-sheet__copy" onClick={() => void copyLink()} aria-label={msg.share.copy}>
                {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
              </button>
            </div>
            {copied && <p className="share-sheet__copied">{msg.share.copied}</p>}
            <p className="share-sheet__meta">
              {msg.share.timeLeft}: {formatDuration(minutesLeft)}
            </p>
            <p className="share-sheet__hint">{msg.share.viewerHint}</p>
            <div className="sheet__actions">
              {typeof navigator !== 'undefined' && navigator.share && (
                <button type="button" className="btn" onClick={() => void navigator.share({ url })}>
                  <Share2 size={16} aria-hidden="true" /> {msg.share.shareLink}
                </button>
              )}
              <button type="button" className="btn btn--accent" onClick={() => void stop()} disabled={busy}>
                {busy ? msg.share.stopping : msg.share.stop}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
